/* ============================================================
   Interfaccia admin: V2 predefinita, classica a richiesta.
   Funzioni pure (testate in adminUiRoutes.test.ts) usate da App.tsx
   per decidere chrome e reindirizzamenti.

   - Gli indirizzi UFFICIALI restano quelli classici (/admin, /admin/associates,
     ...): li usano email, notifiche e segnalibri.
   - In modalità V2 (predefinita) gli indirizzi ufficiali mostrano le pagine V2
     native; le pagine senza versione nativa vengono vestite dal guscio V2.
   - La modalità classica si attiva da /admin/classica[/...] e resta
     memorizzata nel browser; /admin/nuova[/...] torna alla V2.
   - I vecchi indirizzi con suffisso -v2 reindirizzano a quelli ufficiali.
   ============================================================ */

export type AdminUiMode = 'v2' | 'classic';

export const ADMIN_UI_KEY = 'unascaAdminUi';

export const readAdminUiMode = (): AdminUiMode => {
  try {
    return localStorage.getItem(ADMIN_UI_KEY) === 'classic' ? 'classic' : 'v2';
  } catch {
    return 'v2';
  }
};

export const writeAdminUiMode = (mode: AdminUiMode): void => {
  try {
    if (mode === 'classic') localStorage.setItem(ADMIN_UI_KEY, 'classic');
    else localStorage.removeItem(ADMIN_UI_KEY);
  } catch {
    /* storage non disponibile: la scelta vale solo per questa sessione di pagina */
  }
};

const normalize = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.replace(/\/+$/, '') : pathname;

/** true se pathname coincide con prefix o ne è un sotto-percorso (confine di segmento). */
const underPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(prefix + '/');

/* —— Pagine con versione V2 nativa (in modalità V2 hanno il proprio guscio) —— */

export const NATIVE_V2_EXACT: readonly string[] = [
  '/admin',
  '/admin/associates',
  '/admin/memberships',
  '/admin/quote-associative',
  '/admin/pagamenti-tesseramento',
  '/admin/rappresentanti',
  '/admin/cariche-associative',
  '/admin/permessi-associativi',
  '/admin/certified-network',
  '/admin/certified-network/members',
  '/admin/memos',
  '/admin/payments',
  '/admin/voucher/pratiche',
  '/admin/collaborazioni',
  '/admin/svincolo-atti',
  '/admin/voucher-rimborso',
  '/admin/rappresentanti-stanze',
  '/admin/articles',
  '/admin/galleries',
  '/admin/surveys',
  '/admin/service-reviews',
  '/admin/events',
  '/admin/event-types',
  '/admin/dirette',
  '/admin/communications',
  '/admin/recipient-groups',
  '/admin/email-analytics',
  '/admin/push-center',
  '/admin/canali-supporto',
  '/admin/backup',
  '/admin/system-analysis',
  '/admin/users',
];

/** Dettagli nativi "<base>/:id": un solo segmento dopo la base, esclusi i fratelli statici. */
export const NATIVE_V2_DETAIL: readonly { base: string; staticSiblings: readonly string[] }[] = [
  { base: '/admin/associates', staticSiblings: ['import', 'report', 'sedi', 'settings'] },
  { base: '/admin/memberships', staticSiblings: [] },
  { base: '/admin/certified-network/members', staticSiblings: ['activities', 'geocode', 'import', 'link', 'receipts'] },
];

/** Pagine nate V2: hanno sempre il proprio guscio, in qualunque modalità. */
export const ALWAYS_V2_PREFIXES: readonly string[] = [
  '/admin/centro-ticket',
  '/admin/posta-supporto',
  '/admin/guida-ticket',
  '/profile',
];

export const isNativeV2Path = (rawPathname: string): boolean => {
  const pathname = normalize(rawPathname);
  if (NATIVE_V2_EXACT.includes(pathname)) return true;
  return NATIVE_V2_DETAIL.some(({ base, staticSiblings }) => {
    if (!pathname.startsWith(base + '/')) return false;
    const rest = pathname.slice(base.length + 1);
    return rest.length > 0 && !rest.includes('/') && !staticSiblings.includes(rest);
  });
};

export const isAlwaysV2Path = (rawPathname: string): boolean => {
  const pathname = normalize(rawPathname);
  return ALWAYS_V2_PREFIXES.some(p => underPrefix(pathname, p));
};

/* —— Vecchi indirizzi -v2 → indirizzi ufficiali —— */

export const LEGACY_V2_REDIRECTS: readonly (readonly [string, string])[] = [
  ['/admin/dashboard-v2', '/admin'],
  ['/admin/associates-v2', '/admin/associates'],
  ['/admin/memberships-v2', '/admin/memberships'],
  ['/admin/quote-associative-v2', '/admin/quote-associative'],
  ['/admin/pagamenti-tesseramento-v2', '/admin/pagamenti-tesseramento'],
  ['/admin/rappresentanti-v2', '/admin/rappresentanti'],
  ['/admin/cariche-associative-v2', '/admin/cariche-associative'],
  ['/admin/permessi-associativi-v2', '/admin/permessi-associativi'],
  ['/admin/certified-members-v2', '/admin/certified-network/members'],
  ['/admin/memos-v2', '/admin/memos'],
  ['/admin/certified-network-v2', '/admin/certified-network'],
  ['/admin/payments-v2', '/admin/payments'],
  ['/admin/voucher-pratiche-v2', '/admin/voucher/pratiche'],
  ['/admin/collaborazioni-v2', '/admin/collaborazioni'],
  ['/admin/svincolo-atti-v2', '/admin/svincolo-atti'],
  ['/admin/voucher-rimborso-v2', '/admin/voucher-rimborso'],
  ['/admin/rappresentanti-stanze-v2', '/admin/rappresentanti-stanze'],
  ['/admin/articles-v2', '/admin/articles'],
  ['/admin/galleries-v2', '/admin/galleries'],
  ['/admin/surveys-v2', '/admin/surveys'],
  ['/admin/service-reviews-v2', '/admin/service-reviews'],
  ['/admin/events-v2', '/admin/events'],
  ['/admin/event-types-v2', '/admin/event-types'],
  ['/admin/dirette-v2', '/admin/dirette'],
  // La community admin classica vive già su /admin/community-v2 (nome storico, NON è un alias V2)
  ['/admin/community-admin-v2', '/admin/community-v2'],
  ['/admin/communications-v2', '/admin/communications'],
  ['/admin/recipient-groups-v2', '/admin/recipient-groups'],
  ['/admin/email-analytics-v2', '/admin/email-analytics'],
  ['/admin/push-center-v2', '/admin/push-center'],
  ['/admin/canali-supporto-v2', '/admin/canali-supporto'],
  ['/admin/backup-v2', '/admin/backup'],
  ['/admin/system-analysis-v2', '/admin/system-analysis'],
  ['/admin/users-v2', '/admin/users'],
  ['/admin/changelog-v2', '/admin/changelog'],
  ['/admin/activities-v2', '/admin/activities'],
  ['/admin/assistente-v2', '/admin/assistente'],
  ['/admin/categories-v2', '/admin/categories'],
  ['/admin/roles-v2', '/admin/roles'],
  ['/admin/settings-v2', '/admin/settings'],
];

/** Indirizzo ufficiale per un vecchio indirizzo -v2 (sotto-percorso incluso), altrimenti null. */
export const legacyV2ToCanonical = (rawPathname: string): string | null => {
  const pathname = normalize(rawPathname);
  for (const [from, to] of LEGACY_V2_REDIRECTS) {
    if (underPrefix(pathname, from)) {
      const rest = pathname.slice(from.length);
      if (to === '/admin' && rest === '') return '/admin';
      return to + rest;
    }
  }
  return null;
};

/* —— Cambio modalità —— */

export const CLASSIC_SWITCH_BASE = '/admin/classica';
export const V2_SWITCH_BASE = '/admin/nuova';

export const isModeSwitchPath = (rawPathname: string): boolean => {
  const pathname = normalize(rawPathname);
  return underPrefix(pathname, CLASSIC_SWITCH_BASE) || underPrefix(pathname, V2_SWITCH_BASE);
};

/** /admin/classica/associates/12 → /admin/associates/12 (idem per /admin/nuova). */
export const switchPathToCanonical = (rawPathname: string): string => {
  const pathname = normalize(rawPathname);
  for (const base of [CLASSIC_SWITCH_BASE, V2_SWITCH_BASE]) {
    if (underPrefix(pathname, base)) return '/admin' + pathname.slice(base.length);
  }
  return pathname;
};

/** Link "versione classica" di una pagina V2: passa dall'interruttore e torna allo stesso indirizzo. */
export const toClassicHref = (canonicalHref: string): string =>
  underPrefix(canonicalHref, '/admin') ? CLASSIC_SWITCH_BASE + canonicalHref.slice('/admin'.length) : canonicalHref;

/** Link "nuova interfaccia" dalla versione classica, verso lo stesso indirizzo. */
export const toV2Href = (canonicalHref: string): string =>
  underPrefix(canonicalHref, '/admin') ? V2_SWITCH_BASE + canonicalHref.slice('/admin'.length) : canonicalHref;
